"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { memo, useCallback, useState } from "react";
import type { DropInteractionParams } from "../Drop";
import { DropLocation } from "../Drop";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropHeader from "../WaveDropHeader";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropRatings from "../WaveDropRatings";
import WaveDropReactions from "../WaveDropReactions";
import WaveDropReply from "../WaveDropReply";
import WinnerDropBadge from "./WinnerDropBadge";
const getRankColorsByRank = (rank: number | null): {
  borderColor: string;
  textColor: string;
} => {
  const rankNumber = typeof rank === "string" ? parseInt(rank as string, 10) : rank;
  if (rankNumber === 1) {
    return {
      borderColor: "#fbbf24",
      textColor: "#E8D48A"
    };
  }
  if (rankNumber === 2) {
    return {
      borderColor: "#C0C0C0",
      textColor: "#E8E8E8"
    };
  }
  if (rankNumber === 3) {
    return {
      borderColor: "#CD7F32",
      textColor: "#CD7F32"
    };
  }
  return {
    borderColor: "#60606C",
    textColor: "#7F8A93"
  };
};
const getDropStyles = (isActiveDrop: boolean, colors: {
  borderColor: string;
  textColor: string;
}) => {
  if (isActiveDrop) {
    return {
      boxShadow: "inset 1.5px 0 0 rgba(60,203,127,0.7)"
    };
  }
  return {
    boxShadow: `inset 1.5px 0 0 ${colors.borderColor}80,
                inset 0 1px 0 ${colors.borderColor}20,
                inset -1.5px 0 0 ${colors.borderColor}20,
                inset 0 -1.5px 0 ${colors.borderColor}20`
  };
};
interface DefautWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
const DefaultWinnerDrop = t0 => {
  const $ = _c(96);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  const decisionTime = drop.winning_context?.decision_time;
  let handleLongPress;
  let handleOnAddReaction;
  let handleOnReply;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== activePartIndex || $[1] !== drop || $[2] !== effectiveRank || $[3] !== hasTouch || $[4] !== isActiveDrop || $[5] !== location || $[6] !== onReply) {
    const colors = getRankColorsByRank(effectiveRank);
    const getBackgroundColorClass = _temp;
    const bgColorClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getBackgroundColorClass(location);
    let t6;
    if ($[15] !== hasTouch) {
      t6 = () => {
        if (!hasTouch) {
          return;
        }
        setLongPressTriggered(true);
        setIsSlideUp(true);
      };
      $[15] = hasTouch;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    handleLongPress = t6;
    let t7;
    if ($[17] !== activePartIndex || $[18] !== drop || $[19] !== onReply) {
      t7 = () => {
        setIsSlideUp(false);
        onReply({
          drop,
          partId: drop.parts[activePartIndex]?.part_id
        });
      };
      $[17] = activePartIndex;
      $[18] = drop;
      $[19] = onReply;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    handleOnReply = t7;
    let t8;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = () => {
        setIsSlideUp(false);
      };
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    handleOnAddReaction = t8;
    t5 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
    t4 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-lg tw-px-4 tw-py-3 ${bgColorClass}`;
    t1 = "1px solid transparent";
    t2 = "1.5px solid transparent";
    t3 = getDropStyles(isActiveDrop, colors);
    $[0] = activePartIndex;
    $[1] = drop;
    $[2] = effectiveRank;
    $[3] = hasTouch;
    $[4] = isActiveDrop;
    $[5] = location;
    $[6] = onReply;
    $[7] = handleLongPress;
    $[8] = handleOnAddReaction;
    $[9] = handleOnReply;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    handleLongPress = $[7];
    handleOnAddReaction = $[8];
    handleOnReply = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[22] !== t1 || $[23] !== t2 || $[24] !== t3) {
    t6 = {
      border: t1,
      borderLeft: t2,
      ...t3,
      transition: "box-shadow 0.2s ease, background-color 0.2s ease"
    };
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== drop.reply_to || $[27] !== drop.wave || $[28] !== dropViewDropId || $[29] !== onReplyClick) {
    t7 = drop.reply_to && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[26] = drop.reply_to;
    $[27] = drop.wave;
    $[28] = dropViewDropId;
    $[29] = onReplyClick;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== drop) {
    t8 = <WaveDropAuthorPfp drop={drop} />;
    $[31] = drop;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== activePartIndex || $[34] !== drop || $[35] !== isStorm) {
    t9 = <WaveDropHeader drop={drop} showWaveInfo={false} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} />;
    $[33] = activePartIndex;
    $[34] = drop;
    $[35] = isStorm;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const t10 = decisionTime ?? null;
  let t11;
  if ($[37] !== effectiveRank || $[38] !== t10) {
    t11 = <WinnerDropBadge rank={effectiveRank} decisionTime={t10} />;
    $[37] = effectiveRank;
    $[38] = t10;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== drop.wave || $[41] !== showWaveInfo) {
    t12 = showWaveInfo && (() => {
      const waveDetails = drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      };
      const isDirectMessage = waveDetails.chat?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={_temp2} className="tw-mt-0.5 tw-text-xs tw-leading-none tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[40] = drop.wave;
    $[41] = showWaveInfo;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t11 || $[44] !== t12 || $[45] !== t9) {
    t13 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-y-1">{t9}{t11}{t12}</div>;
    $[43] = t11;
    $[44] = t12;
    $[45] = t9;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== activePartIndex || $[48] !== drop || $[49] !== handleLongPress || $[50] !== hasTouch || $[51] !== onDropContentClick || $[52] !== onQuoteClick) {
    t14 = <div><WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} onLongPress={handleLongPress} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} /></div>;
    $[47] = activePartIndex;
    $[48] = drop;
    $[49] = handleLongPress;
    $[50] = hasTouch;
    $[51] = onDropContentClick;
    $[52] = onQuoteClick;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== t13 || $[55] !== t14) {
    t15 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t13}{t14}</div>;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== t15 || $[58] !== t8) {
    t16 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t8}{t15}</div>;
    $[57] = t15;
    $[58] = t8;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== activePartIndex || $[61] !== drop || $[62] !== handleOnReply || $[63] !== isMobile || $[64] !== showReplyAndQuote) {
    t17 = !isMobile && showReplyAndQuote && <div className="tw-absolute tw-right-0 tw-top-1"><WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} /></div>;
    $[60] = activePartIndex;
    $[61] = drop;
    $[62] = handleOnReply;
    $[63] = isMobile;
    $[64] = showReplyAndQuote;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== drop.metadata) {
    t18 = drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />;
    $[66] = drop.metadata;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  let t19;
  if ($[68] !== drop) {
    t19 = !!drop.raters_count && <WaveDropRatings drop={drop} />;
    $[68] = drop;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  let t20;
  if ($[70] !== drop) {
    t20 = <WaveDropReactions drop={drop} />;
    $[70] = drop;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== t19 || $[73] !== t20) {
    t21 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t19}{t20}</div>;
    $[72] = t19;
    $[73] = t20;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  let t22;
  if ($[75] !== t18 || $[76] !== t21) {
    t22 = <div className="tw-ml-[3.25rem] tw-mt-1.5 tw-flex tw-flex-col tw-gap-2">{t18}{t21}</div>;
    $[75] = t18;
    $[76] = t21;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  let t23;
  if ($[78] !== t16 || $[79] !== t17 || $[80] !== t22 || $[81] !== t4 || $[82] !== t6 || $[83] !== t7) {
    t23 = <div className={t4} style={t6}>{t7}{t16}{t17}{t22}</div>;
    $[78] = t16;
    $[79] = t17;
    $[80] = t22;
    $[81] = t4;
    $[82] = t6;
    $[83] = t7;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  let t24;
  if ($[85] !== drop || $[86] !== handleOnAddReaction || $[87] !== handleOnReply || $[88] !== isSlideUp || $[89] !== longPressTriggered || $[90] !== showReplyAndQuote) {
    t24 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[85] = drop;
    $[86] = handleOnAddReaction;
    $[87] = handleOnReply;
    $[88] = isSlideUp;
    $[89] = longPressTriggered;
    $[90] = showReplyAndQuote;
    $[91] = t24;
  } else {
    t24 = $[91];
  }
  let t25;
  if ($[92] !== t23 || $[93] !== t24 || $[94] !== t5) {
    t25 = <div className={t5}>{t23}{t24}</div>;
    $[92] = t23;
    $[93] = t24;
    $[94] = t5;
    $[95] = t25;
  } else {
    t25 = $[95];
  }
  return t25;
};
export default memo(DefaultWinnerDrop);
function _temp(loc) {
  return loc === DropLocation.WAVE ? "tw-bg-iron-900/60" : "tw-bg-iron-950";
}
function _temp2(e) {
  return e.stopPropagation();
}